import { createContext, useCallback, useContext } from 'react';
import { useFieldArray } from 'react-hook-form';
import { type DragEndEvent } from '@dnd-kit/core';
import { shuffle } from 'es-toolkit';
import { nanoid } from 'nanoid';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { confirmDialog } from '@/ui_v3';
import { MAX_QUIZ_COUNT } from '../constant';
import { useBuildFormContext } from '../form/schema';
import { type BuildQuizListItem } from '../form/schema/BuildQuizListSchema';
import { getBlankQuizItem } from '../form/schema/getBlankQuizItem';
import { useBuildQuestionAlert } from '../hook/useBuildQuestionAlert';
import { focusField } from './focus/focusUtils';
import { type QuizType } from './type';

/**
 * 퀴즈 생성, 수정, 삭제, 복사 등의 기능을 제공합니다.
 * @returns
 */
const useBuildQuiz = () => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    control,
    getValues
  } = useBuildFormContext();
  const {
    fields: quizList,
    append,
    remove,
    insert,
    move,
    replace
  } = useFieldArray({
    control,
    name: 'quizList',
    keyName: '_rhfId' // useFieldArray 내부에서 생성하는 keyName은 사용하지 않습니다.
  });
  const {
    openQuestionCountLimitAlert
  } = useBuildQuestionAlert();
  const handleInsert = useCallback((index: number, quizzes: BuildQuizListItem[]) => {
    insert(index, quizzes);
    focusField(`quizList.${index}.question`, {
      delay: 200
    });
  }, [insert]);
  const handleCopy = useCallback((index: number) => {
    const quizList = getValues('quizList');
    if (quizList.length >= MAX_QUIZ_COUNT) {
      openQuestionCountLimitAlert();
      return;
    }
    const copiedQuizId = nanoid();
    const targetQuiz = quizList[index];
    insert(index + 1, {
      ...targetQuiz,
      id: copiedQuizId
    });
    focusField(`quizList.${index + 1}.question`, {
      delay: 200
    });
  }, [insert, openQuestionCountLimitAlert, getValues]);
  const handleDelete = useCallback(async (index: number, eventProperties?: Record<string, string>) => {
    const confirmed = await confirmDialog.openAsync({
      header: {
        title: t('build:build.deleteConfirm.title')
      },
      confirmText: t('common:global.delete'),
      confirmIntent: 'destructive'
    });
    if (!confirmed) return;
    remove(index);
    const targetIndex = index - 1 < 0 ? 0 : index - 1;
    focusField(`quizList.${targetIndex}.question`, {
      scroll: true,
      focus: false
    });
    logger.track('remove_quizItem', {
      ...eventProperties
    });
  }, [remove, t]);
  const handleCreate = useCallback((type: QuizType, index?: number) => {
    if (quizList.length >= MAX_QUIZ_COUNT) {
      openQuestionCountLimitAlert();
      return;
    }
    let createdQuiz: ReturnType<typeof getBlankQuizItem>;
    const blankQuizItem = getBlankQuizItem();
    switch (type) {
      case 'multiple':
        createdQuiz = {
          ...blankQuizItem,
          type: 'multiple'
        };
        break;
      case 'subjective':
        createdQuiz = {
          ...blankQuizItem,
          type: 'subjective'
        };
        break;
      case 'ox':
        createdQuiz = {
          ...blankQuizItem,
          type: 'ox' as const
        };
        break;
      case 'essay':
        createdQuiz = {
          ...blankQuizItem,
          type: 'essay'
        };
        break;
      case 'match':
        createdQuiz = {
          ...blankQuizItem,
          type: 'match'
        };
        break;
    }
    if (typeof index === 'number') {
      insert(index + 1, createdQuiz);
      focusField(`quizList.${index + 1}.question`, {
        delay: 200
      });
      return;
    }
    append(createdQuiz);
    focusField(`quizList.${quizList.length}.question`, {
      delay: 200
    });
  }, [quizList, append, openQuestionCountLimitAlert, insert]);
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = quizList.findIndex(i => i.id === active.id);
    const newIndex = quizList.findIndex(i => i.id === over.id);
    move(oldIndex, newIndex);
    logger.track('quiz_switch');
  }, [quizList, move]);
  const handleShuffle = useCallback(() => {
    const quizList = getValues('quizList');
    const shuffledQuizList = shuffle(quizList);
    replace(shuffledQuizList as BuildQuizListItem[]);
  }, [getValues, replace]);
  const replaceQuizList = useCallback((nextQuizList: BuildQuizListItem[]) => {
    replace(nextQuizList);
  }, [replace]);
  return {
    handleCopy,
    handleDelete,
    handleCreate,
    handleDragEnd,
    handleInsert,
    handleShuffle,
    replaceQuizList,
    quizList
  };
};
const QuizListContext = createContext<ReturnType<typeof useBuildQuiz>>({
  handleCopy: () => {},
  handleDelete: async () => {},
  handleCreate: () => {},
  handleDragEnd: () => {},
  handleInsert: () => {},
  handleShuffle: () => {},
  replaceQuizList: () => {},
  quizList: []
});
export const QuizListProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const methods = useBuildQuiz();
  return <QuizListContext.Provider value={methods} data-sentry-element="unknown" data-sentry-component="QuizListProvider" data-sentry-source-file="useQuizList.tsx">
      {children}
    </QuizListContext.Provider>;
};
export const useQuizList = () => {
  return useContext(QuizListContext);
};